<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        @click="handleSubmit"
      >
        Confirm
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    Confirm your deactivation
  </DialogHeader>
  <DialogContent>
    <span class="font-secondary">{{ copy }}</span>
    <span class="font-secondary">
      Once confirmed, you'll receive an email notifying you that your account
      has been deactivated.
    </span>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-full tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-full tablet-h:w-auto"
      danger
      :loading="loading"
      @click="handleSubmit"
    >
      Yes, deactivate
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { UiButton, Icon } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { useDeactivateAccountMutation } from '@/shared/graphql/legacy/generated'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { signout } from '@dacr/auth'
import { computed } from 'vue'
import { useBillingStore } from '@/features/billing/store'
import { formatPrice } from '@/shared/utils/money'
import { useRouter } from 'vue-router'

const { close } = useDialog()
const { open, closeAll } = useDialog()

const { mutate: deactivateAccount, loading } = useDeactivateAccountMutation()

const router = useRouter()
const billingStore = useBillingStore()
const webviewStore = useWebviewStore()

const hasOutstandingBalance = computed(() =>
  billingStore.billing?.upcomingDetails.upcomingBilling?.breakdown.some(
    (breakdown) => breakdown.outstanding
  )
)

const outstandingBalance = computed(() => {
  const totalOutstanding =
    billingStore.billing?.upcomingDetails.upcomingBilling?.breakdown.reduce(
      (acc, breakdown) =>
        (acc += breakdown.outstanding ? breakdown.amount || 0 : 0),
      0
    ) || 0

  return formatPrice({
    amount: totalOutstanding / 100,
    currency: billingStore.billing?.upcomingDetails.upcomingBilling?.currency,
  })
})

const copy = computed(() => {
  if (hasOutstandingBalance.value) {
    return `Are you sure you want to deactivate your account and pay the outstanding balance of ${outstandingBalance.value}?`
  }

  return 'Please confirm you would like to deactivate your account.'
})

const continueFlow = () => {
  signout()

  window.localStorage.clear()

  if (webviewStore.isActive) {
    webviewStore.postMessage({
      type: NativeActionsEnum.SignOut,
    })
  } else {
    router.push('/signout')
  }
}

const handleSubmit = async () => {
  if (hasOutstandingBalance.value) {
    open(Dialogs.Pay, {
      events: {
        async success() {
          await deactivateAccount()

          open(Dialogs.Success, {
            props: {
              title: 'Your account was deactivated',
              html: '<div class="mb-[14px]">You will receive an email once your payment has been processed.</div> You\'ll be signed out of all your devices upon selecting "Okay". You can activate your account at any time by logging back in to Dacr.',
              preventClose: true,
            },
            events: {
              success() {
                closeAll()
                continueFlow()
              },
            },
          })
        },
      },
    })
  } else {
    await deactivateAccount()

    open(Dialogs.Success, {
      props: {
        title: 'Your account was deactivated',
        html: 'You\'ll be signed out of all your devices upon selecting "Okay". You can activate your account at any time by logging back in to Dacr.',
        preventClose: true,
      },
      events: {
        success() {
          closeAll()
          continueFlow()
        },
      },
    })
  }
}
</script>
